import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import {Draw} from "ol/interaction";
import {Fill, Stroke, Style} from "ol/style";
import CircleStyle from "ol/style/Circle";
import {unByKey} from "ol/Observable";
import {Overlay} from "ol";
import {getLength} from "ol/sphere";
import {nanoid} from "nanoid";

export class Ruler {
    /**
     * @type {EpMap|null}
     */
    epMap = null;
    /**
     * @type {VectorSource|null}
     */
    source = null;
    layer = null;
    drawInteraction = null;
    tooltip = null;
    tooltipElement = null;

    constructor(epMap) {
        this.epMap = epMap;

        this.source = new VectorSource();

        this.layer = new VectorLayer({
            source: this.source,
            style: {
                'fill-color': 'rgba(255, 255, 255, 0.2)',
                'stroke-color': '#ffcc33',
                'stroke-width': 2,
                'circle-radius': 7,
                'circle-fill-color': '#ffcc33',
            },
        });

        this.epMap.map.addLayer(this.layer);

        this.epMap.map.on('contextmenu', (e) => {
            if (this.drawInteraction) {
                this.drawInteraction.appendCoordinates([e.coordinate]);
                this.finish()
            }
        })
    }

    start(firstPosition = null) {
        if (this.drawInteraction) {
            this.cancel();
            return;
        }

        this.drawInteraction = new Draw({
            source: this.source,
            type: 'LineString',
            condition: function (e) {
                // when the point's button is 1(leftclick), allows drawing
                return e.originalEvent.buttons === 1;


            },
            style: new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0.2)',
                }),
                stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.5)',
                    lineDash: [10, 10],
                    width: 2,
                }),
                image: new CircleStyle({
                    radius: 5,
                    stroke: new Stroke({
                        color: 'rgba(0, 0, 0, 0.7)',
                    }),
                    fill: new Fill({
                        color: 'rgba(255, 255, 255, 0.2)',
                    }),
                }),
            })
        })
        this.epMap.map.addInteraction(this.drawInteraction);

        let sketch, listener;

        this.drawInteraction.on('drawstart', (evt) => {
            // set sketch
            sketch = evt.feature;

            /** @type {import("../src/ol/coordinate.js").Coordinate|undefined} */
            let tooltipCoord = evt.coordinate;

            listener = sketch.getGeometry().on('change', (evt) => {
                const geom = evt.target;
                let output = this._formatLength(geom);
                tooltipCoord = geom.getLastCoordinate();
                this.tooltipElement.innerHTML = output;
                this.tooltip.setPosition(tooltipCoord);
            });

            this.epMap.helpTooltip.set('Aby zakończyć, kliknij dwa razy na ostatni punkt');
        });

        this.drawInteraction.on('drawend', (e) => {
            let id = nanoid();

            this.tooltip.set('feature-id', id);
            this.tooltipElement.className = 'ol-tooltip ol-tooltip-static';
            this.tooltip.setOffset([0, -7]);

            // unset sketch
            sketch = null;

            // unset tooltip so that a new one can be created
            this.tooltipElement = null;
            unByKey(listener);

            e.feature.set('type', 'ruler-measure');
            e.feature.setId(id);

            this.finish();
        });

        this._createTooltip();

        if (firstPosition) {
            this.drawInteraction.appendCoordinates([firstPosition]);
        }
    }

    finish() {
        if (this.drawInteraction) {
            this.drawInteraction.finishDrawing();
            this.epMap.map.removeInteraction(this.drawInteraction);

            this.drawInteraction = null;
        }

        this.epMap.helpTooltip.clear();
    }

    cancel() {
        if (this.drawInteraction) {
            this.drawInteraction.abortDrawing();
            this.epMap.map.removeInteraction(this.drawInteraction);

            this.drawInteraction = null;

            this.epMap.map.removeOverlay(this.tooltip);
            this.tooltip = null;
        }
    }

    getNumberOfMeasurements() {
        return this.source.getFeatures().length;
    }

    clear() {
        this.source.getFeatures().forEach(feature => this.removeMeasure(feature.getId()))
    }

    removeMeasure(id) {
        let feature = this.source.getFeatureById(id);

        if (feature) {
            this.source.removeFeature(feature);
        }

        this.epMap.map.getOverlays().forEach(overlay => {
            if(overlay && overlay.get('feature-id') === id) {
                this.epMap.map.removeOverlay(overlay);
            }
        })
    }

    _createTooltip() {
        if (this.tooltipElement) {
            this.tooltipElement.parentNode.removeChild(this.tooltipElement);
        }

        this.tooltipElement = document.createElement('div');
        this.tooltipElement.className = 'ol-tooltip ol-tooltip-measure';
        this.tooltip = new Overlay({
            element: this.tooltipElement,
            offset: [0, -15],
            positioning: 'bottom-center',
            stopEvent: false,
            insertFirst: false,
        });

        this.epMap.map.addOverlay(this.tooltip);
    }

    _formatLength = (line) => {
        const length = getLength(line);
        let output;
        if (length > 100) {
            output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km';
        } else {
            output = Math.round(length * 100) / 100 + ' ' + 'm';
        }
        return output;
    };
}